import * as _ from 'lodash-es';
import { useCallback, useReducer } from 'react';
import useEventListener from '../../utils/useEventListener';
import type { TableDocumentColumn } from './types';

const MIN_WIDTH = 65;

interface State {
  columnId: string;
  initial: { offset: number; width: number };
  width?: number;
}

const reducer = (
  state: State | null,
  action:
    | {
        type: 'start';
        columnId: string;
        initial: { offset: number; width: number };
      }
    | { type: 'move'; offset: number }
    | { type: 'end' },
): State | null => {
  switch (action.type) {
    case 'start':
      return {
        columnId: action.columnId,
        initial: action.initial,
      };
    case 'move':
      return (
        state && {
          ...state,
          width: Math.max(
            action.offset - state.initial.offset + state.initial.width,
            MIN_WIDTH,
          ),
        }
      );
    case 'end':
      return null;
    default:
      return state;
  }
};

const useResizableColumns = ({
  resizeColumn,
  columns,
}: {
  resizeColumn: (columnId: string, width: number) => void;
  columns: TableDocumentColumn[];
}) => {
  const [resizedColumn, dispatch] = useReducer(reducer, null);
  const onColumnResizeStart = useCallback(
    (columnId: string, initial: { offset: number; width: number }) => {
      dispatch({ type: 'start', columnId, initial });
    },
    [],
  );

  useEventListener(
    document.body,
    'mousemove',
    (e: MouseEvent) => {
      dispatch({ type: 'move', offset: e.pageX });
    },
    {
      skip: !resizedColumn,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      options: { passive: true } as any,
    },
  );

  useEventListener(
    document.body,
    'mouseup',
    () => {
      if (resizedColumn?.columnId && resizedColumn.width) {
        resizeColumn(resizedColumn.columnId, resizedColumn.width);
      }
      dispatch({ type: 'end' });
    },
    { skip: !resizedColumn },
  );

  // <table/> does not respect td's min-width, so we need to set the min width of the table
  // and let it assign widths to columns.
  // See 17.5.2.1 on https://www.w3.org/TR/CSS2/tables.html#width-layout for table layout algorithms.
  const minTableWidth = _.sum(
    columns.map((column) => {
      if (resizedColumn?.columnId === column.insert.id && resizedColumn.width) {
        return resizedColumn.width;
      }
      return column.attributes?.width || MIN_WIDTH;
    }),
  );

  return { minTableWidth, resizedColumn, onColumnResizeStart };
};

export default useResizableColumns;
